import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, InputBase, TextField, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { ROUTES } from '../../../types/global/routes.types';
import { useDispatch, useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import {
    addBrandOpsKnowledge,
    fetchBrandOpsKnowledge,
    removeBrandOpsKnowledge,
    runBrandOpsAiPlayground,
} from '../../../redux/brandOps/actions';
import {
    brandOpsAiBusySelector,
    brandOpsAiResultSelector,
    brandOpsFetchingKnowledgeSelector,
    brandOpsKnowledgeSelector,
    brandOpsMutatingKnowledgeSelector,
} from '../../../redux/brandOps/selectors';
import { showToastMessage, useDispatchPromise } from '../../../utils/AppUtils';
import { getAiReplyState } from '../../../redux/brandOps/brandOpsHelpers';
import KnowledgeDropZone from './KnowledgeDropZone';
import { brandModalOutlinedInputSx, BRAND_LUXE_MUTED, BRAND_LUXE_PRIMARY } from '../brandDashboardTokens';
import {
    BrandManageGate,
    ManageGoldButton,
    ManagePageHeader,
    ManageSurface,
    MANAGE_PAGE_SX,
    StatusChip,
    useBrandManageGate,
} from './manageShared';

const DOC_STATUS_COLORS: Record<string, { bg: string; border: string; text: string }> = {
    ready: { bg: 'rgba(52,211,153,0.1)', border: 'rgba(52,211,153,0.3)', text: '#34d399' },
    processing: { bg: 'rgba(255,201,101,0.1)', border: 'rgba(255,201,101,0.25)', text: '#ffc965' },
    failed: { bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.25)', text: '#f87171' },
};

type InputTab = 'upload' | 'paste';

export default function BrandManageAiPage() {
    const dispatch = useDispatch();
    const dispatchP = useDispatchPromise();
    const { brandId } = useBrandManageGate();
    const docs = useSelector(brandOpsKnowledgeSelector);
    const fetching = useSelector(brandOpsFetchingKnowledgeSelector);
    const mutating = useSelector(brandOpsMutatingKnowledgeSelector);
    const aiBusy = useSelector(brandOpsAiBusySelector);
    const aiResult = useSelector(brandOpsAiResultSelector);

    const [title, setTitle] = useState('');
    const [excerpt, setExcerpt] = useState('');
    const [prompt, setPrompt] = useState('');
    const [inputTab, setInputTab] = useState<InputTab>('upload');
    const [chatHistory, setChatHistory] = useState<{ role: 'user' | 'ai'; text: string }[]>([]);
    const chatEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (brandId) dispatch(fetchBrandOpsKnowledge({ brandId, force: true }));
    }, [brandId, dispatch]);

    const addDoc = async () => {
        if (!brandId || !title.trim()) return;
        try {
            await dispatchP(
                addBrandOpsKnowledge({
                    brandId,
                    body: { title: title.trim(), text_excerpt: excerpt.trim() || undefined },
                }),
            );
            setTitle('');
            setExcerpt('');
            showToastMessage('Knowledge doc added', 'success');
        } catch (err: any) {
            showToastMessage(err?.message || 'Could not add document', 'error');
        }
    };

    const removeDoc = async (docId: string) => {
        if (!brandId) return;
        try {
            await dispatchP(removeBrandOpsKnowledge({ brandId, docId }));
            showToastMessage('Removed', 'success');
        } catch (err: any) {
            showToastMessage(err?.message || 'Remove failed', 'error');
        }
    };

    const runPlayground = async () => {
        const msg = prompt.trim();
        if (!brandId || !msg) return;
        setChatHistory(h => [...h, { role: 'user', text: msg }]);
        setPrompt('');
        try {
            const result: any = await dispatchP(
                runBrandOpsAiPlayground({ brandId, message: msg, format: 'instagram_dm' }),
            );
            const state = result ? getAiReplyState(result) : null;
            const replyText = state?.kind === 'reply' ? state.text : state?.message ?? 'No reply generated.';
            setChatHistory(h => [...h, { role: 'ai', text: replyText }]);
            setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
        } catch (err: any) {
            setChatHistory(h => [...h, { role: 'ai', text: '⚠️ ' + (err?.message || 'Playground failed') }]);
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const replyState = aiResult ? getAiReplyState(aiResult) : null;
    const trained = docs.reduce((n, d) => n + (d.token_estimate || 0), 0);

    return (
        <BrandManageGate>
            <Box sx={MANAGE_PAGE_SX}>
                <ManagePageHeader
                    title="AI Engine"
                    subtitle="Teach T.I. about your brand—upload guides, FAQs, and tone notes so Instagram DM replies match how you actually sound."
                    back={ROUTES.BRAND_MANAGE}
                />

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    <ManageSurface sx={{ p: 0, overflow: 'hidden' }}>
                        {/* Header */}
                        <Box
                            sx={{
                                px: 2.5,
                                pt: 2.5,
                                pb: 2,
                                background:
                                    'linear-gradient(135deg, rgba(255,201,101,0.08) 0%, rgba(139,92,246,0.06) 100%)',
                                borderBottom: '1px solid rgba(255,255,255,0.06)',
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Box
                                    sx={{
                                        p: 0.75,
                                        borderRadius: 1.5,
                                        bgcolor: 'rgba(255,201,101,0.15)',
                                        display: 'flex',
                                    }}>
                                    <AutoStoriesOutlinedIcon sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 18 }} />
                                </Box>
                                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', fontSize: 15 }}>
                                    Knowledge Base
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        px: 1.5,
                                        py: 0.75,
                                        borderRadius: 2,
                                        bgcolor: 'rgba(52,211,153,0.1)',
                                        border: '1px solid rgba(52,211,153,0.2)',
                                    }}>
                                    <Typography
                                        sx={{ color: '#34d399', fontSize: 15, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                        {docs.length}
                                    </Typography>
                                    <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 10 }}>docs</Typography>
                                </Box>
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        px: 1.5,
                                        py: 0.75,
                                        borderRadius: 2,
                                        bgcolor: 'rgba(139,92,246,0.1)',
                                        border: '1px solid rgba(139,92,246,0.2)',
                                    }}>
                                    <Typography
                                        sx={{ color: '#a78bfa', fontSize: 15, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                        {(trained / 1000).toFixed(1)}k
                                    </Typography>
                                    <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 10 }}>
                                        tokens{fetching ? '…' : ''}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Tab toggle */}
                        <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    width: 'fit-content',
                                }}>
                                {(
                                    [
                                        { key: 'upload', label: 'Upload file', Icon: CloudUploadOutlinedIcon },
                                        { key: 'paste', label: 'Paste text', Icon: EditNoteOutlinedIcon },
                                    ] as { key: InputTab; label: string; Icon: any }[]
                                ).map(({ key, label, Icon }) => (
                                    <Box
                                        key={key}
                                        onClick={() => setInputTab(key)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            px: 2,
                                            py: 0.875,
                                            cursor: 'pointer',
                                            fontSize: 12,
                                            transition: 'all 0.15s',
                                            bgcolor: inputTab === key ? 'rgba(255,201,101,0.12)' : 'transparent',
                                            color: inputTab === key ? BRAND_LUXE_PRIMARY : BRAND_LUXE_MUTED,
                                            borderRight: key === 'upload' ? '1px solid rgba(255,255,255,0.08)' : 'none',
                                            '&:hover': { color: inputTab === key ? BRAND_LUXE_PRIMARY : '#fff' },
                                        }}>
                                        <Icon sx={{ fontSize: 15 }} />
                                        {label}
                                    </Box>
                                ))}
                            </Box>

                            {/* Upload panel */}
                            {inputTab === 'upload' && (
                                <Box sx={{ mt: 1.5 }}>
                                    <KnowledgeDropZone brandId={brandId} disabled={mutating} />
                                </Box>
                            )}

                            {/* Paste panel */}
                            {inputTab === 'paste' && (
                                <Box sx={{ mt: 1.5 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Title"
                                        value={title}
                                        onChange={e => setTitle(e.target.value)}
                                        sx={{
                                            ...brandModalOutlinedInputSx,
                                            mb: 1.5,
                                            label: { color: BRAND_LUXE_MUTED },
                                            '& .MuiInputLabel-root.Mui-focused': { color: BRAND_LUXE_PRIMARY },
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={4}
                                        label="Paste brand guide / FAQ excerpt"
                                        value={excerpt}
                                        onChange={e => setExcerpt(e.target.value)}
                                        sx={{
                                            ...brandModalOutlinedInputSx,
                                            mb: 1.5,
                                            label: { color: BRAND_LUXE_MUTED },
                                            '& .MuiInputLabel-root.Mui-focused': { color: BRAND_LUXE_PRIMARY },
                                        }}
                                    />
                                    <ManageGoldButton
                                        fullWidth
                                        onClick={addDoc}
                                        disabled={mutating || !title.trim()}
                                        sx={{ py: 1.25 }}>
                                        Add to vault
                                    </ManageGoldButton>
                                </Box>
                            )}
                        </Box>

                        {/* Docs list */}
                        <Box sx={{ px: 2.5, pb: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {docs.length > 0 && (
                                <Typography
                                    sx={{
                                        color: BRAND_LUXE_MUTED,
                                        fontSize: 11,
                                        mb: 0.5,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.06em',
                                    }}>
                                    Vault contents
                                </Typography>
                            )}
                            {docs.map(d => {
                                const sc = DOC_STATUS_COLORS[d.status] ?? DOC_STATUS_COLORS.processing;
                                return (
                                    <Box
                                        key={d._id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1.5,
                                            p: 1.5,
                                            borderRadius: 2,
                                            bgcolor: sc.bg,
                                            border: `1px solid ${sc.border}`,
                                        }}>
                                        <ArticleOutlinedIcon
                                            sx={{ color: sc.text, fontSize: 18, mt: 0.25, flexShrink: 0 }}
                                        />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                sx={{
                                                    color: '#fff',
                                                    fontSize: 13,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    lineHeight: 1.3,
                                                    mb: 0.4,
                                                }}>
                                                {d.title}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    flexWrap: 'wrap',
                                                }}>
                                                <Typography
                                                    sx={{
                                                        color: sc.text,
                                                        fontSize: 11,
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    }}>
                                                    {d.status === 'processing' ? 'extracting…' : d.status}
                                                </Typography>
                                                {d.status === 'ready' && (d.token_estimate ?? 0) > 0 && (
                                                    <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11 }}>
                                                        ~{d.token_estimate!.toLocaleString()} tokens
                                                    </Typography>
                                                )}
                                                {d.status === 'failed' && (
                                                    <Typography sx={{ color: '#f87171', fontSize: 11 }}>
                                                        Extraction failed — remove and re-upload
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        <IconButton
                                            size="small"
                                            onClick={() => removeDoc(d._id)}
                                            sx={{
                                                color: BRAND_LUXE_MUTED,
                                                p: 0.5,
                                                '&:hover': { color: '#f87171' },
                                                flexShrink: 0,
                                            }}>
                                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                );
                            })}
                            {!docs.length && !fetching && (
                                <Box sx={{ textAlign: 'center', py: 3 }}>
                                    <AutoStoriesOutlinedIcon
                                        sx={{ color: 'rgba(255,255,255,0.1)', fontSize: 32, mb: 1 }}
                                    />
                                    <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 13 }}>
                                        No knowledge docs yet
                                    </Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: 11, mt: 0.5 }}>
                                        Upload a file or paste text above to train the AI
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </ManageSurface>

                    <ManageSurface sx={{ display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden' }}>
                        {/* Header */}
                        <Box
                            sx={{
                                px: 2.5,
                                pt: 2,
                                pb: 1.5,
                                borderBottom: '1px solid rgba(255,255,255,0.06)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}>
                            <Box>
                                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', fontSize: 14 }}>
                                    IG DM playground
                                </Typography>
                                <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11, mt: 0.25 }}>
                                    Simulate an inbound DM and see how the AI replies
                                </Typography>
                            </Box>
                            {aiResult && (
                                <StatusChip
                                    label={aiResult.configured ? 'Claude configured' : 'Keys empty'}
                                    tone={aiResult.configured ? 'mint' : 'gold'}
                                />
                            )}
                        </Box>

                        {/* Suggestion chips */}
                        {chatHistory.length === 0 && (
                            <Box sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                {[
                                    'How much does it cost?',
                                    'How do I sign up?',
                                    'Do you work with micro-influencers?',
                                    'What platforms do you support?',
                                    'How long does a campaign take?',
                                    'Can I see examples of past work?',
                                ].map(s => (
                                    <Box
                                        key={s}
                                        onClick={() => setPrompt(s)}
                                        sx={{
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: 5,
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            color: BRAND_LUXE_MUTED,
                                            fontSize: 11.5,
                                            cursor: 'pointer',
                                            transition: 'all 0.15s',
                                            '&:hover': {
                                                borderColor: 'rgba(255,201,101,0.4)',
                                                color: BRAND_LUXE_PRIMARY,
                                                bgcolor: 'rgba(255,201,101,0.06)',
                                            },
                                        }}>
                                        {s}
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {/* Chat messages */}
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 280,
                                maxHeight: 380,
                                overflowY: 'auto',
                                px: 2,
                                py: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                            }}>
                            {chatHistory.length === 0 ? (
                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: 13 }}>
                                        Pick a suggestion or type a message below
                                    </Typography>
                                </Box>
                            ) : (
                                chatHistory.map((msg, i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: msg.role === 'user' ? 'flex-start' : 'flex-end',
                                        }}>
                                        <Box
                                            sx={{
                                                maxWidth: '78%',
                                                px: 1.75,
                                                py: 1,
                                                borderRadius:
                                                    msg.role === 'user' ? '4px 16px 16px 16px' : '16px 4px 16px 16px',
                                                bgcolor:
                                                    msg.role === 'user'
                                                        ? 'rgba(255,255,255,0.07)'
                                                        : 'rgba(255,201,101,0.12)',
                                                border:
                                                    msg.role === 'user'
                                                        ? '1px solid rgba(255,255,255,0.08)'
                                                        : '1px solid rgba(255,201,101,0.2)',
                                            }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 11,
                                                    color: msg.role === 'user' ? BRAND_LUXE_MUTED : BRAND_LUXE_PRIMARY,
                                                    mb: 0.4,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                }}>
                                                {msg.role === 'user' ? 'User' : 'AI (Brand)'}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: 13,
                                                    color: '#fff',
                                                    whiteSpace: 'pre-wrap',
                                                    lineHeight: 1.55,
                                                }}>
                                                {msg.text}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))
                            )}
                            {aiBusy && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Box
                                        sx={{
                                            px: 2,
                                            py: 1.25,
                                            borderRadius: '16px 4px 16px 16px',
                                            bgcolor: 'rgba(255,201,101,0.08)',
                                            border: '1px solid rgba(255,201,101,0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}>
                                        <CircularProgress size={12} sx={{ color: BRAND_LUXE_PRIMARY }} />
                                        <Typography sx={{ fontSize: 12, color: BRAND_LUXE_MUTED }}>
                                            Thinking…
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            <div ref={chatEndRef} />
                        </Box>

                        {/* Input */}
                        <Box
                            sx={{
                                px: 2,
                                py: 1.5,
                                borderTop: '1px solid rgba(255,255,255,0.06)',
                                display: 'flex',
                                alignItems: 'flex-end',
                                gap: 1,
                            }}>
                            <InputBase
                                fullWidth
                                multiline
                                maxRows={4}
                                placeholder="Type a simulated DM…"
                                value={prompt}
                                onChange={e => setPrompt(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        runPlayground();
                                    }
                                }}
                                sx={{
                                    color: '#fff',
                                    fontSize: 13,
                                    flex: 1,
                                    '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.25)' },
                                }}
                            />
                            <IconButton
                                onClick={runPlayground}
                                disabled={aiBusy || !prompt.trim()}
                                sx={{
                                    color: prompt.trim() ? BRAND_LUXE_PRIMARY : 'rgba(255,255,255,0.2)',
                                    p: 0.75,
                                    transition: 'color 0.15s',
                                }}>
                                <SendIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Box>

                        {aiResult && (
                            <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11, px: 2, pb: 1.25 }}>
                                Trained tokens: {aiResult.trained_tokens ?? 0} · Docs: {aiResult.docs_count ?? 0}
                            </Typography>
                        )}
                    </ManageSurface>
                </Box>
            </Box>
        </BrandManageGate>
    );
}
